/* eslint-disable @typescript-eslint/camelcase */
import React, {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useEffect,
} from "react";

import { useUpdateClaimMutation } from "src/api/claims/updateClaim.generated";
import {
  MENTORSHIP_PROFILE_PIPELINE_ID,
  MENTORSHIP_PROFILE_STAGE_ID,
  MENTORSHIP_REQUEST_STAGE_ID_CLAIMED,
  MENTORSHIP_REQUEST_STAGE_ID_NOTIFIED,
  MENTORSHIP_REQUEST_STAGE_ID_RESOLVED,
} from "src/shared/constants/mentorship";
import * as MentorshipRequests from "src/shared/contexts/MentorshipRequestContext/types";
import { getFieldAnswer, parseState } from "src/shared/utils/hackerapi";
import { HackerAPIFile } from "src/shared/utils/hackerapi";

import { useUserContext } from "../UserContext";

import { useCreateMentorProfileMutation } from "./graphql/createMentorProfile.generated";
import { useGetClaimedRequestsLazyQuery } from "./graphql/getClaimedRequests.generated";
import { useGetMentorshipIndividualLazyQuery } from "./graphql/getMentorIndividual.generated";
import { useUpdateProfilePhotoMutation } from "./graphql/updateProfilePhoto.generated";
import * as Mentor from "./types";

export interface MentorContextState {
  profile: Mentor.Profile | null;
  isLoading: boolean;
  loadError?: string;
  isUpdating: boolean;
  updateError?: string;
  updateProfile: (
    data: Partial<MentorContextState["profile"]>
  ) => Promise<unknown>;
  updateProfilePhoto: (fileIds: number[]) => Promise<unknown>;
  updateRequest: (data: MentorshipRequests.Request) => Promise<unknown>; //TODO
  claimedRequests: MentorshipRequests.Request[];
  createProfile: () => Promise<unknown>;
  refetchClaimedRequests: () => void;
  // refetchProfile: () => Promise<unknown>;
}

const DEFAULT_STATE: MentorContextState = {
  profile: null,
  isLoading: false,
  isUpdating: false,
  updateProfile: () => Promise.reject("No parent MentorContext found"),
  updateProfilePhoto: () => Promise.reject(),
  updateRequest: () =>
    Promise.reject("No parent MentorshipRequestContextProvider found"),
  claimedRequests: [],
  createProfile: () => Promise.reject("No parent MentorContext found"),
  refetchClaimedRequests: () => {},
  // refetchProfile: () => Promise.reject("No parent MentorContext found"),
};

const MentorContext: React.Context<MentorContextState> = createContext(
  DEFAULT_STATE
);

MentorContext.displayName = "MentorContext";

export const useMentorContext = () => useContext(MentorContext);

export const MentorContextProvider: React.FC = ({ children }) => {
  const { id, info } = useUserContext();

  const [
    getMentorshipIndividualQuery,
    { data: mentorData, loading, error },
  ] = useGetMentorshipIndividualLazyQuery();
  const [
    updateClaimMutation,
    { loading: updatingProfile, error: updateProfileError },
  ] = useUpdateClaimMutation();
  const [
    getClaimedRequestsQuery,
    { data: requestData, loading: requestsLoading },
  ] = useGetClaimedRequestsLazyQuery();
  const [
    createProfileMutation,
    { loading: createProfileLoading },
  ] = useCreateMentorProfileMutation();

  useEffect(() => {
    if (id) {
      getMentorshipIndividualQuery({
        variables: {
          userId: id.toString(),
        },
      });
      getClaimedRequestsQuery({
        variables: {
          mentorId: id.toString(),
          claimedStageId: MENTORSHIP_REQUEST_STAGE_ID_CLAIMED,
          notifiedStageId: MENTORSHIP_REQUEST_STAGE_ID_NOTIFIED,
          resolvedStageId: MENTORSHIP_REQUEST_STAGE_ID_RESOLVED,
        },
      });
    }
  }, [id, getClaimedRequestsQuery, getMentorshipIndividualQuery]);

  const refetchClaimedRequests: MentorContextState["refetchClaimedRequests"] = useCallback(() => {
    if (id) {
      getClaimedRequestsQuery({
        variables: {
          mentorId: id.toString(),
          claimedStageId: MENTORSHIP_REQUEST_STAGE_ID_CLAIMED,
          notifiedStageId: MENTORSHIP_REQUEST_STAGE_ID_NOTIFIED,
          resolvedStageId: MENTORSHIP_REQUEST_STAGE_ID_RESOLVED,
        },
      });
    }
  }, [id, getClaimedRequestsQuery]);

  const createProfile: MentorContextState["createProfile"] = useCallback(() => {
    if (id && info) {
      return createProfileMutation({
        variables: {
          profileData: {
            pipeline_id: MENTORSHIP_PROFILE_PIPELINE_ID,
            stage_id: MENTORSHIP_PROFILE_STAGE_ID,
            name: info.name,
            answers: [
              {
                slug: "user_id",
                answer: id,
              },
            ],
          },
        },
      });
    }
    return Promise.resolve();
  }, [createProfileMutation, info, id]);

  const profile: MentorContextState["profile"] = useMemo(() => {
    const individualClaim = mentorData?.claims[0];
    const individualFields = individualClaim?.fields;
    return individualClaim && individualFields
      ? {
          user_id: id as number,
          institution: getFieldAnswer(
            individualFields,
            "institution"
          ) as string,
          mentorship_profile_photo: getFieldAnswer(
            individualFields,
            "mentorship_profile_photo"
          ) as HackerAPIFile,
          mentorship_topics: getFieldAnswer(
            individualFields,
            "mentorship_topics"
          ) as string[],
        }
      : null;
  }, [mentorData, id]);

  const updateProfile: MentorContextState["updateProfile"] = useCallback(
    (data: Partial<MentorContextState["profile"]>) => {
      if (mentorData) {
        const parsedData = parseState(data);
        return updateClaimMutation({
          variables: {
            updatedData: {
              id: mentorData.claims[0].id as number,
              answers: parsedData,
            },
          },
        });
      }
      return Promise.resolve();
    },
    [mentorData, updateClaimMutation]
  );

  const updateRequest: MentorContextState["updateRequest"] = useCallback(
    (data: MentorshipRequests.Request) => {
      const {
        id,
        title,
        description,
        category,
        priority,
        stage,
        mentor_id,
      } = data;
      const stage_id = MentorshipRequests.Stage[stage];
      const answers = parseState({
        title,
        description,
        category,
        priority,
        mentor_id,
      });
      return updateClaimMutation({
        variables: {
          updatedData: {
            id,
            stage_id,
            answers,
          },
        },
      });
    },
    [updateClaimMutation]
  );

  const [updateProfilePhotoMutation] = useUpdateProfilePhotoMutation();

  const updateProfilePhoto: MentorContextState["updateProfilePhoto"] = useCallback(
    (fileIds: number[]) => {
      if (mentorData) {
        return updateProfilePhotoMutation({
          variables: {
            mentorClaimId: mentorData.claims[0].id as number,
            fileIds,
          },
        });
      }

      return Promise.resolve({});
    },
    [mentorData, updateProfilePhotoMutation]
  );

  const claimedRequests: MentorContextState["claimedRequests"] = useMemo(
    () =>
      (requestData?.claims || []).map((request) => ({
        id: request.id,
        title: getFieldAnswer(request.fields, "title"),
        description: getFieldAnswer(request.fields, "description"),
        category: getFieldAnswer(request.fields, "category"),
        priority: getFieldAnswer(request.fields, "priority"),
        created_at: request.created_at,
        reopened: getFieldAnswer(request.fields, "reopened"),
        mentee_name: getFieldAnswer(request.fields, "mentee_name"),
        mentee_id: getFieldAnswer(request.fields, "mentee_id"),
        mentor_id: getFieldAnswer(request.fields, "mentor_id"),
        stage: MentorshipRequests.Stage[request.stage_id],
        mentee_discord_id: getFieldAnswer(request.fields, "mentee_discord_tag"),
      })),
    [requestData]
  );

  const mentorState = {
    profile,
    isLoading:
      loading || requestsLoading || updatingProfile || createProfileLoading,
    loadError: error?.message,
    isUpdating: updatingProfile,
    updateError: updateProfileError?.message,
    updateProfile,
    updateProfilePhoto,
    updateRequest,
    claimedRequests,
    createProfile,
    // refetchProfile,
    refetchClaimedRequests,
  };

  return (
    <MentorContext.Provider value={mentorState}>
      {children}
    </MentorContext.Provider>
  );
};
